import { useState, useCallback, useMemo } from "react";
import { useTenant } from "../../../../hooks/useTenant";
import { usePagination } from "../../../../hooks/usePagination";
import { COLLECTIONS } from "../../../../services/collections";
import FinancialRecordService from "../../../../services/financial_record_service";
import { PAGE_SIZE } from "../constants";
import { FINANCIAL_STATUS, FINANCIAL_TYPES } from "../../../../constants/financial";

/**
 * Retorna a data de hoje em formato "YYYY-MM-DD".
 */
const today = () => new Date().toISOString().split("T")[0];

/**
 * useReceivables
 * Hook que carrega financialRecords do tipo INCOME (Contas a Receber).
 * Por padrão carrega apenas o DIA ATUAL (econômico em leituras Firebase).
 * O gestor pode alterar o período via filtros de data.
 */
export const useReceivables = () => {
    const { idTenant, user: authUser } = useTenant();

    // Filtros
    const [statusFilter, setStatusFilter] = useState("");
    const [search, setSearch] = useState("");
    const [startDate, setStartDate] = useState(today());
    const [endDate, setEndDate] = useState(today());

    // State de ações
    const [processing, setProcessing] = useState(false);

    // Filtros do Firestore — inclui range de dueDate para economizar leituras
    const firestoreFilters = useMemo(() => {
        const filters = [
            { field: "type", operator: "==", value: FINANCIAL_TYPES.INCOME },
            { field: "dueDate", operator: ">=", value: startDate },
            { field: "dueDate", operator: "<=", value: endDate },
        ];
        if (statusFilter) {
            filters.push({ field: "status", operator: "==", value: statusFilter });
        }
        return filters;
    }, [statusFilter, startDate, endDate]);

    // Paginação com filtros de data
    const {
        data: records,
        loading,
        error,
        hasMore,
        loadMore,
        refresh,
    } = usePagination(
        `tenants/${idTenant}/${COLLECTIONS.FINANCIAL_RECORDS}`,
        idTenant,
        {
            pageSize: PAGE_SIZE,
            orderField: "dueDate",
            orderDirection: "desc",
            filters: firestoreFilters,
        }
    );

    // Filtragem local por busca textual
    const filteredRecords = useMemo(() => {
        if (!search.trim()) return records;
        const term = search.toLowerCase().trim();
        return records.filter(r =>
            (r.memberName || "").toLowerCase().includes(term) ||
            (r.description || "").toLowerCase().includes(term) ||
            (r.entityName || "").toLowerCase().includes(term) ||
            (r.saleNumber || "").toLowerCase().includes(term)
        );
    }, [records, search]);

    /**
     * Dar baixa (marcar como PAID) com dados completos.
     */
    const markAsPaid = useCallback(async (recordId, paymentData = {}) => {
        if (!idTenant || !recordId) return false;
        setProcessing(true);

        try {
            await FinancialRecordService.markAsPaid(recordId, idTenant, authUser, {
                paymentDate: paymentData.paymentDate || today(),
                idBankAccount: paymentData.idBankAccount || null,
                bankAccountName: paymentData.bankAccountName || "",
                paymentMethod: paymentData.paymentMethod || null,
                amountPaid: paymentData.amountPaid ? Number(paymentData.amountPaid) : null,
                observation: paymentData.observation || "",
                documentNumber: paymentData.documentNumber || "",
            });
            refresh();
            return true;
        } catch (err) {
            console.error("[useReceivables] Erro ao dar baixa:", err);
            return false;
        } finally {
            setProcessing(false);
        }
    }, [idTenant, authUser, refresh]);

    /**
     * Cancelar um recebível.
     */
    const cancelRecord = useCallback(async (recordId, reason = "") => {
        if (!idTenant || !recordId) return false;
        setProcessing(true);

        try {
            await FinancialRecordService.cancel(recordId, idTenant, authUser, reason);
            refresh();
            return true;
        } catch (err) {
            console.error("[useReceivables] Erro ao cancelar:", err);
            return false;
        } finally {
            setProcessing(false);
        }
    }, [idTenant, authUser, refresh]);

    // Totais do período carregado
    const summary = useMemo(() => {
        let totalPending = 0;
        let totalOverdue = 0;
        let totalPaid = 0;
        let countPending = 0;
        let countOverdue = 0;

        records.forEach(r => {
            if (r.status === FINANCIAL_STATUS.PENDING) {
                totalPending += r.grossAmount || 0;
                countPending++;
            } else if (r.status === FINANCIAL_STATUS.OVERDUE) {
                totalOverdue += r.grossAmount || 0;
                countOverdue++;
            } else if (r.status === FINANCIAL_STATUS.PAID) {
                totalPaid += r.netAmount || r.grossAmount || 0;
            }
        });

        return { totalPending, totalOverdue, totalPaid, countPending, countOverdue };
    }, [records]);

    return {
        records: filteredRecords,
        allRecords: records,
        summary,
        loading,
        error,
        hasMore,
        processing,
        statusFilter,
        search,
        startDate,
        endDate,
        setStatusFilter,
        setSearch,
        setStartDate,
        setEndDate,
        actions: {
            loadMore,
            refresh,
            markAsPaid,
            cancelRecord,
        },
    };
};
